import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { 
  Factory, 
  CheckCircle, 
  AlertCircle, 
  XCircle,
  ChevronDown,
  ChevronRight,
  Wrench,
  RefreshCw,
  Image as ImageIcon,
  X,
  ChevronLeft
} from 'lucide-react';
import { useMechanicalDashboardData } from '@/hooks/useMechanicalDashboardData';
import { PendingIssuesTable } from './PendingIssuesTable';

const SUPABASE_URL = import.meta.env.VITE_TASKBLOOM_SUPABASE_URL || 'https://mxtkabejdsoqkgtqjaaw.supabase.co';

interface Equipment {
  id: string;
  code: string;
  name: string;
  location: string;
  latest_report_status: 'passed' | 'warning' | 'failed' | null;
  latest_report_id: string | null;
  last_report_date: string | null;
  last_status: string | null;
}

interface WorkUnitData {
  id: string;
  unit_name: string;
  total_equipment: number;
  passed_count: number;
  warning_count: number;
  failed_count: number;
  equipment: Equipment[];
  name: string;
  description: string | null;
  normal_equipment: number;
  warning_equipment: number;
  failed_equipment: number;
}

interface Photo {
  id: string;
  photo_url: string;
  photo_type: string;
  description: string | null;
  photo_order: number;
}

interface ReportItem {
  id: string;
  check_result: 'pass' | 'fail';
  notes: string | null;
  item_order: number;
  is_resolved?: boolean;
  resolved_at?: string | null;
  maintenance_checklists: {
    item_name: string;
    description: string | null;
  } | null;
}

interface ReportDetail {
  id: string;
  equipment_id: string;
  equipment: {
    id: string;
    code: string;
    name: string;
    location: string;
  };
  report_date: string;
  status: string;
  overall_notes: string | null;
  inspector_name: string | null;
  maintenance_report_items: ReportItem[];
  maintenance_report_photos: Photo[];
  created_at: string;
  updated_at: string;
}

interface ImageSlideModalProps {
  photos: Array<{
    id: string;
    photo_url: string;
    description: string | null;
  }>;
  initialIndex: number;
  isOpen: boolean;
  onClose: () => void;
}

const ImageSlideModal = ({ photos, initialIndex, isOpen, onClose }: ImageSlideModalProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setCurrentIndex(initialIndex);
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, [initialIndex]);

  useEffect(() => {
    // Reset zoom when changing images
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, [currentIndex]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      if (e.key === 'ArrowLeft') {
        handlePrev();
      } else if (e.key === 'ArrowRight') {
        handleNext();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex, photos.length]);

  useEffect(() => {
    if (!isOpen) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      const delta = e.deltaY * -0.01;
      setScale((prevScale) => {
        const newScale = Math.min(Math.max(0.5, prevScale + delta), 5);
        
        // Reset position if zooming out to 1 or less
        if (newScale <= 1) {
          setPosition({ x: 0, y: 0 });
        }
        
        return newScale;
      });
    };

    // Small delay to ensure the ref is set
    const timeoutId = setTimeout(() => {
      const container = imageContainerRef.current;
      if (container) {
        container.addEventListener('wheel', handleWheel, { passive: false });
      }
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      const container = imageContainerRef.current;
      if (container) {
        container.removeEventListener('wheel', handleWheel);
      }
    };
  }, [isOpen]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : photos.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < photos.length - 1 ? prev + 1 : 0));
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleResetZoom = () => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  if (!isOpen || photos.length === 0) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] h-[95vh] p-0 overflow-hidden gap-0 border-0 [&>button]:hidden">
        <div className="relative w-full h-full bg-black flex flex-col" style={{ height: '95vh', maxHeight: '95vh' }}>
          {/* Close Button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 z-50 bg-black/50 hover:bg-black/70 text-white rounded-full"
            onClick={onClose}
          >
            <X className="h-6 w-6" />
          </Button>

          {/* Image Counter */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 bg-black/70 text-white px-4 py-2 rounded-full text-sm font-medium">
            {currentIndex + 1} / {photos.length}
          </div>

          {/* Zoom Indicator */}
          {scale !== 1 && (
            <div className="absolute top-16 left-1/2 -translate-x-1/2 z-50 bg-black/70 text-white px-3 py-1 rounded-full text-xs">
              {Math.round(scale * 100)}%
            </div>
          )}

          {/* Reset Zoom Button */}
          {scale !== 1 && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute top-4 left-4 z-50 bg-black/50 hover:bg-black/70 text-white text-xs"
              onClick={handleResetZoom}
            >
              Reset Zoom
            </Button>
          )}

          {/* Main Image with Click Areas */}
          <div 
            className="relative flex items-center justify-center flex-1 overflow-hidden"
            ref={imageContainerRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{ 
              cursor: scale > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in',
              minHeight: 0,
              flex: '1 1 0'
            }}
          >
            {/* Left Click Area */}
            {photos.length > 1 && scale === 1 && (
              <div
                className="absolute left-0 top-0 bottom-0 w-1/2 z-30 cursor-pointer group"
                onClick={handlePrev}
              >
                {/* Left Navigation Button - shows on hover */}
                <div className="absolute left-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="bg-black/50 hover:bg-black/70 text-white rounded-full h-12 w-12 flex items-center justify-center">
                    <ChevronLeft className="h-8 w-8" />
                  </div>
                </div>
              </div>
            )}

            {/* Right Click Area */}
            {photos.length > 1 && scale === 1 && (
              <div
                className="absolute right-0 top-0 bottom-0 w-1/2 z-30 cursor-pointer group"
                onClick={handleNext}
              >
                {/* Right Navigation Button - shows on hover */}
                <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="bg-black/50 hover:bg-black/70 text-white rounded-full h-12 w-12 flex items-center justify-center">
                    <ChevronRight className="h-8 w-8" />
                  </div>
                </div>
              </div>
            )}

            {/* Image */}
            <img
              src={photos[currentIndex].photo_url}
              alt={photos[currentIndex].description || `Image ${currentIndex + 1}`}
              className="select-none"
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
                transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                pointerEvents: scale > 1 ? 'none' : 'auto',
              }}
              draggable={false}
            />
          </div>

          {/* Footer Section */}
          <div className="flex-shrink-0 bg-black" style={{ maxHeight: '30vh' }}>
            {/* Description */}
            {photos[currentIndex].description && (
              <div className="bg-black/80 text-white p-2 text-center border-t border-gray-700">
                <p className="text-xs line-clamp-2">{photos[currentIndex].description}</p>
              </div>
            )}

            {/* Thumbnail Strip */}
            {photos.length > 1 && (
              <div className="bg-black/60 py-2 px-4 border-t border-gray-700">
                <div className="flex justify-center overflow-x-auto scrollbar-thin scrollbar-thumb-gray-600">
                  <div className="flex gap-2 py-1">
                    {photos.map((photo, index) => (
                      <button
                        key={photo.id}
                        onClick={() => setCurrentIndex(index)}
                        className={`flex-shrink-0 w-14 h-14 rounded-md overflow-hidden border-2 transition-all ${
                          index === currentIndex
                            ? 'border-white scale-110 shadow-lg'
                            : 'border-transparent opacity-60 hover:opacity-100 hover:border-gray-400'
                        }`}
                      >
                        <img
                          src={photo.photo_url}
                          alt={`Thumbnail ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const MechanicalDashboardTab: React.FC = () => {
  const { data: workUnits, isLoading, error, refetch } = useMechanicalDashboardData();
  const [expandedUnit, setExpandedUnit] = useState<string | null>(null);
  const [expandedEquipment, setExpandedEquipment] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [reportDetails, setReportDetails] = useState<Record<string, ReportDetail>>({});
  const [loadingReport, setLoadingReport] = useState<string | null>(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(-1);
  const [currentReportPhotos, setCurrentReportPhotos] = useState<Photo[]>([]);

  // Auto-expand first unit on load
  useEffect(() => {
    if (workUnits && workUnits.length > 0 && !expandedUnit) {
      setExpandedUnit(workUnits[0].id);
    }
  }, [workUnits, expandedUnit]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await refetch();
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleToggleEquipment = async (equipmentId: string, reportId: string | null) => {
    if (expandedEquipment === equipmentId) {
      setExpandedEquipment(null);
      return;
    }

    setExpandedEquipment(equipmentId);

    // Fetch report details if not already fetched and reportId exists
    if (reportId && !reportDetails[reportId]) {
      setLoadingReport(reportId);
      try {
        const response = await fetch(
          `${SUPABASE_URL}/functions/v1/mechanical-report-detail?report_id=${reportId}`
        );
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setReportDetails(prev => ({ ...prev, [reportId]: data }));
      } catch (error) {
        console.error('Error fetching report details:', error);
      } finally {
        setLoadingReport(null);
      }
    }
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'passed':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Bình thường</Badge>;
      case 'not_inspected':
        return <Badge className="bg-yellow-500"><AlertCircle className="w-3 h-3 mr-1" />Chưa KT</Badge>;
      case 'failed':
        return <Badge className="bg-red-500"><AlertCircle className="w-3 h-3 mr-1" />Lỗi</Badge>;
      default:
        return <Badge variant="outline">Không rõ</Badge>;
    }
  };

  // Helper: Get date color based on days since inspection
  const getDateColorClass = (reportDate: string) => {
    const now = new Date();
    const inspectionDate = new Date(reportDate);
    const daysDiff = Math.floor((now.getTime() - inspectionDate.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysDiff <= 3) {
      return 'text-green-600 font-bold'; // Green for <= 3 days
    } else if (daysDiff <= 7) {
      return 'text-yellow-600 font-bold'; // Yellow for 3-7 days
    } else {
      return 'text-red-600 font-bold'; // Red for > 7 days
    }
  };

  // Helper: Calculate days since inspection
  const getDaysSinceInspection = (reportDate: string) => {
    const now = new Date();
    const inspectionDate = new Date(reportDate);
    return Math.floor((now.getTime() - inspectionDate.getTime()) / (1000 * 60 * 60 * 24));
  };

  const getCheckResultIcon = (result: 'pass' | 'fail') => {
    return result === 'pass' 
      ? <CheckCircle className="w-4 h-4 text-green-600" />
      : <XCircle className="w-4 h-4 text-red-600" />;
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400 mb-4" />
        <p className="text-gray-500">Đang tải dữ liệu dashboard cơ điện...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="mt-4">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Lỗi tải dữ liệu dashboard: {error.message}
        </AlertDescription>
      </Alert>
    );
  }

  if (!workUnits || workUnits.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Factory className="h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-500 text-center">
            Không có dữ liệu xưởng
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-center items-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Dashboard Cơ Điện</h2>
          <p className="text-gray-500 mt-1">Tổng quan thiết bị và tình trạng kiểm tra</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {workUnits.map((unit) => (
          <Card
            key={unit.id}
            className={`cursor-pointer transition-all duration-300 border-2 ${
              expandedUnit === unit.id
                ? 'shadow-xl scale-105 border-green-600 ring-2 ring-green-500/50'
                : 'hover:shadow-xl hover:scale-105 hover:border-green-600'
            }`}
            onClick={() => setExpandedUnit(expandedUnit === unit.id ? null : unit.id)}
          >
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <Factory className="w-8 h-8 text-green-600" />
                  </div>
                  <CardTitle className="text-xl font-bold">{unit.name}</CardTitle>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-gray-900">{unit.total_equipment}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide">Thiết bị</div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-3">
                {/* Normal */}
                <div className="text-center p-3 bg-green-50 rounded-lg border border-green-200">
                  <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-1" />
                  <div className="text-2xl font-bold text-green-700">{unit.normal_equipment}</div>
                  <div className="text-xs text-green-600 font-medium whitespace-nowrap">OK</div>
                </div>
                
                {/* Failed - Gray if 0, Red if > 0 */}
                <div className={`text-center p-3 rounded-lg border ${
                  unit.failed_equipment > 0 
                    ? 'bg-red-50 border-red-200' 
                    : 'bg-gray-50 border-gray-200'
                }`}>
                  <XCircle className={`w-5 h-5 mx-auto mb-1 ${
                    unit.failed_equipment > 0 ? 'text-red-600' : 'text-gray-400'
                  }`} />
                  <div className={`text-2xl font-bold ${
                    unit.failed_equipment > 0 ? 'text-red-700' : 'text-gray-600'
                  }`}>{unit.failed_equipment}</div>
                  <div className={`text-xs font-medium whitespace-nowrap ${
                    unit.failed_equipment > 0 ? 'text-red-600' : 'text-gray-500'
                  }`}>Lỗi</div>
                </div>
                
                {/* Not Inspected - Gray if 0, Yellow if > 0 */}
                <div className={`text-center p-3 rounded-lg border ${
                  unit.warning_equipment > 0 
                    ? 'bg-yellow-50 border-yellow-200' 
                    : 'bg-gray-50 border-gray-200'
                }`}>
                  <AlertCircle className={`w-5 h-5 mx-auto mb-1 ${
                    unit.warning_equipment > 0 ? 'text-yellow-600' : 'text-gray-400'
                  }`} />
                  <div className={`text-2xl font-bold ${
                    unit.warning_equipment > 0 ? 'text-yellow-700' : 'text-gray-600'
                  }`}>{unit.warning_equipment}</div>
                  <div className={`text-xs font-medium whitespace-nowrap ${
                    unit.warning_equipment > 0 ? 'text-yellow-600' : 'text-gray-500'
                  }`}>Chưa KT</div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="space-y-2">
                <div className="flex justify-between text-xs text-gray-500">
                  <span>Tình trạng tổng quan</span>
                  <span>{unit.total_equipment > 0 ? Math.round((unit.normal_equipment / unit.total_equipment) * 100) : 0}% OK</span>
                </div>
                <div className="h-3 bg-gray-200 rounded-full overflow-hidden shadow-inner">
                  <div className="h-full flex">
                    <div
                      className="bg-gradient-to-r from-green-500 to-green-600 transition-all duration-500"
                      style={{ width: `${unit.total_equipment > 0 ? (unit.normal_equipment / unit.total_equipment) * 100 : 0}%` }}
                    />
                    <div
                      className="bg-gradient-to-r from-yellow-500 to-yellow-600 transition-all duration-500"
                      style={{ width: `${unit.total_equipment > 0 ? (unit.warning_equipment / unit.total_equipment) * 100 : 0}%` }}
                    />
                    <div
                      className="bg-gradient-to-r from-red-500 to-red-600 transition-all duration-500"
                      style={{ width: `${unit.total_equipment > 0 ? (unit.failed_equipment / unit.total_equipment) * 100 : 0}%` }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Expanded Work Unit Details */}
      {expandedUnit && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Factory className="w-5 h-5" />
              Chi tiết thiết bị - {workUnits.find(w => w.id === expandedUnit)?.name}
            </CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              Danh sách thiết bị và tình trạng kiểm tra mới nhất
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {workUnits
                .find(w => w.id === expandedUnit)
                ?.equipment.map((eq) => {
                  const reportDetail = eq.latest_report_id ? reportDetails[eq.latest_report_id] : null;
                  const isEquipmentExpanded = expandedEquipment === eq.id;

                  return (
                    <div key={eq.id} className="border rounded-lg overflow-hidden">
                      {/* Equipment Header */}
                      <div 
                        className="flex items-center justify-between p-3 hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => handleToggleEquipment(eq.id, eq.latest_report_id)}
                      >
                        <div className="flex items-center gap-2 flex-1">
                          {isEquipmentExpanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
                          <Wrench className="w-4 h-4 text-gray-500" />
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{eq.code}</span>
                              <span className="text-sm text-gray-900 font-medium">- {eq.name}</span>
                            </div>
                            {eq.last_report_date && (
                              <div className="text-xs flex items-center gap-2">
                                <span className="text-gray-500">Kiểm tra:</span>
                                <span className={getDateColorClass(eq.last_report_date)}>
                                  {new Date(eq.last_report_date).toLocaleDateString('vi-VN', { 
                                    day: '2-digit', 
                                    month: '2-digit', 
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </span>
                                <span className="text-gray-400">-</span>
                                <span className={getDateColorClass(eq.last_report_date)}>
                                  Kiểm tra lần cuối cách đây: {getDaysSinceInspection(eq.last_report_date)} ngày
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {getStatusBadge(eq.last_status)}
                        </div>
                      </div>

                      {/* Expanded Detail */}
                      {isEquipmentExpanded && (
                        <div className="border-t bg-gray-50 p-4">
                          {!eq.latest_report_id ? (
                            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                              <AlertCircle className="w-12 h-12 mb-3 text-gray-400" />
                              <p className="text-sm font-medium">Chưa có báo cáo kiểm tra nào</p>
                              <p className="text-xs mt-1">Thiết bị này chưa được kiểm tra</p>
                            </div>
                          ) : loadingReport === eq.latest_report_id ? (
                            <div className="flex items-center justify-center py-8">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                            </div>
                          ) : reportDetail ? (
                            <div className="space-y-4">
                              {/* Report Info */}
                              <div className="bg-white p-3 rounded-lg border">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <span className="text-gray-500">Người kiểm tra:</span>
                                    <span className="ml-2 font-medium">{reportDetail.inspector_name}</span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Ngày kiểm tra:</span>
                                    <span className={`ml-2 ${getDateColorClass(reportDetail.report_date)}`}>
                                      {new Date(reportDetail.report_date).toLocaleDateString('vi-VN')}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Checklist Items */}
                              {reportDetail.maintenance_report_items.length > 0 && (
                                <div className="bg-white rounded-lg border overflow-hidden">
                                  <div className="bg-gray-100 px-4 py-2 border-b">
                                    <h4 className="font-semibold text-sm">Kết quả kiểm tra</h4>
                                  </div>
                                  <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                      <thead className="bg-gray-50 border-b">
                                        <tr>
                                          <th className="px-4 py-2 text-left font-medium text-gray-700 w-12">STT</th>
                                          <th className="px-4 py-2 text-left font-medium text-gray-700">Hạng mục kiểm tra</th>
                                          <th className="px-4 py-2 text-center font-medium text-gray-700 w-32">Kết quả</th>
                                          <th className="px-4 py-2 text-left font-medium text-gray-700">Ghi chú</th>
                                          <th className="px-4 py-2 text-center font-medium text-gray-700 w-32">Đã xử lý</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {reportDetail.maintenance_report_items
                                          .sort((a, b) => a.item_order - b.item_order)
                                          .map((item, index) => (
                                            <tr key={item.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                              <td className="px-4 py-2 text-center">{index + 1}</td>
                                              <td className="px-4 py-2">
                                                <div className="flex flex-col gap-1">
                                                  <span className="font-medium text-gray-900">{item.maintenance_checklists?.item_name || '—'}</span>
                                                  {item.notes && (
                                                    <span className="text-xs font-bold text-red-600">
                                                      =&gt; {item.notes}
                                                    </span>
                                                  )}
                                                  {item.resolution_notes && (
                                                    <div className="flex items-center gap-1 text-xs text-black font-bold">
                                                      <span>{item.resolution_notes}</span>
                                                      <CheckCircle className="w-3 h-3 text-green-600 flex-shrink-0" />
                                                    </div>
                                                  )}
                                                </div>
                                              </td>
                                              <td className="px-4 py-2">
                                                <div className="flex items-center justify-center gap-2">
                                                  {getCheckResultIcon(item.check_result)}
                                                  <span className="font-medium">
                                                    {item.check_result === 'pass' ? 'Đạt' : 'Không đạt'}
                                                  </span>
                                                </div>
                                              </td>
                                              <td className="px-4 py-2 text-gray-600">{item.notes || '—'}</td>
                                              <td className="px-4 py-2">
                                                <div className="flex items-center justify-center">
                                                  {item.check_result === 'fail' ? (
                                                    item.is_resolved ? (
                                                      <CheckCircle className="w-5 h-5 text-green-600" />
                                                    ) : (
                                                      <XCircle className="w-5 h-5 text-red-600" />
                                                    )
                                                  ) : (
                                                    <span className="text-gray-400">—</span>
                                                  )}
                                                </div>
                                              </td>
                                            </tr>
                                          ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}

                              {/* Overall Notes */}
                              {reportDetail.overall_notes && (
                                <div className="bg-white p-4 rounded-lg border">
                                  <h4 className="font-semibold text-sm mb-2">Ghi chú tổng quan</h4>
                                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{reportDetail.overall_notes}</p>
                                </div>
                              )}

                              {/* Photos */}
                              {reportDetail.maintenance_report_photos.length > 0 && (
                                <div className="bg-white p-4 rounded-lg border">
                                  <div className="flex items-center gap-2 mb-3">
                                    <ImageIcon className="w-4 h-4 text-gray-600" />
                                    <h4 className="font-semibold text-sm">Hình ảnh ({reportDetail.maintenance_report_photos.length})</h4>
                                  </div>
                                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                                    {reportDetail.maintenance_report_photos
                                      .sort((a, b) => a.photo_order - b.photo_order)
                                      .map((photo, index) => (
                                        <div 
                                          key={photo.id}
                                          className="border rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                                          onClick={() => {
                                            setSelectedImageIndex(index);
                                            setCurrentReportPhotos(reportDetail.maintenance_report_photos);
                                          }}
                                        >
                                          <img 
                                            src={photo.photo_url} 
                                            alt={photo.description || 'Equipment photo'}
                                            className="w-full h-32 object-cover"
                                          />
                                          {photo.description && (
                                            <div className="p-2 bg-gray-50 text-xs text-gray-600 border-t">
                                              {photo.description}
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
                              <XCircle className="w-12 h-12 mb-3 text-gray-400" />
                              <p className="text-sm font-medium">Không thể tải dữ liệu báo cáo</p>
                              <p className="text-xs mt-1">Vui lòng thử lại sau</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>

    {/* Pending Issues Table */}
    <PendingIssuesTable />

    {/* Image Slide Modal */}
    {selectedImageIndex >= 0 && currentReportPhotos.length > 0 && (
      <ImageSlideModal
        photos={currentReportPhotos
          .sort((a, b) => a.photo_order - b.photo_order)
          .map(p => ({
            id: p.id,
            photo_url: p.photo_url,
            description: p.description
          }))}
        initialIndex={selectedImageIndex}
        isOpen={selectedImageIndex >= 0}
        onClose={() => setSelectedImageIndex(-1)}
      />
    )}
    </>
  );
};

export default MechanicalDashboardTab;
